import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {useCallback, useEffect} from 'react';

import {useRefreshId} from '../../util/hook';
import {usePagination} from '../../util/pagination-hook/pagination-hook';
import {useApiHooks} from '../api-hook/api-hook';

import {
    createIntegrationWebhook,
    deleteIntegrationWebhook,
    fetchIntegrationWebhook,
    fetchIntegrationWebhooks,
    updateIntegrationWebhook,
} from './webhook-integration-api';
import {
    CreateWebhookIntegrationType,
    UpdateWebhookIntegrationType,
    WebhookIntegrationItemType,
} from './webhook-integration-type';

export function useWebhookIntegrations(enabled = true): {
    isLoading: boolean;
    data: Array<WebhookIntegrationItemType> | null;
    refetch: () => void;
    loadMoreItems: () => void;
    removeItemFromList: (id: number) => void;
} {
    const {onDataLoadFailed, onDataLoaded, pageSize, onChange, page, total} = usePagination({
        dependencies: null,
        shouldSaveState: false,
        initialPageSize: 10,
    });

    const {result, setResult, isInProgress, setIsInProgress, reset} = useApiHooks<Array<WebhookIntegrationItemType>>();
    const {refresh, refreshId} = useRefreshId();

    const refetch = useCallback(() => {
        onChange(1);
        refresh();
    }, [onChange, refresh]);

    useEffect(() => {
        if (!enabled) {
            reset();
            return;
        }

        setIsInProgress(true);

        fetchIntegrationWebhooks({
            page,
            pageSize,
        })
            .finally(() => {
                setIsInProgress(false);
            })
            .then((apiResult) => {
                if (page === 1) {
                    setResult(apiResult.results);
                } else {
                    setResult((previousResults) => {
                        return [
                            ...(previousResults || []),
                            ...apiResult.results.filter(({id}) => !previousResults?.some((item) => item.id === id)),
                        ];
                    });
                }

                onDataLoaded(apiResult);
            })
            .catch(onDataLoadFailed);
    }, [enabled, refreshId, onDataLoadFailed, onDataLoaded, page, pageSize, reset, setIsInProgress, setResult]);

    const loadMoreItems = useCallback(() => {
        if (isInProgress) {
            return;
        }

        if (pageSize * page < total) {
            onChange(page + 1);
        }
    }, [isInProgress, onChange, page, pageSize, total]);

    const removeItemFromList = useCallback(
        (id: number) => {
            setResult(result?.filter((item) => item.id !== id) || null);
        },
        [result, setResult]
    );

    return {
        isLoading: isInProgress,
        data: result,
        refetch,
        loadMoreItems,
        removeItemFromList,
    };
}

export function useWebhookIntegration(webhookId?: number | null): UseQueryResult<WebhookIntegrationItemType> {
    return useQuery(['webhookIntegration', webhookId], () => fetchIntegrationWebhook(webhookId || 0), {
        enabled: Boolean(webhookId),
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnMount: false,
    });
}

export function useRemoveWebhookMutation(): UseMutationResult<void, unknown, number> {
    return useMutation({
        mutationFn: deleteIntegrationWebhook,
    });
}

export function useCreateWebhookMutation(): UseMutationResult<void, unknown, CreateWebhookIntegrationType> {
    return useMutation({
        mutationFn: createIntegrationWebhook,
    });
}

export function useUpdateWebhookMutation(): UseMutationResult<void, unknown, UpdateWebhookIntegrationType> {
    return useMutation({
        mutationFn: updateIntegrationWebhook,
    });
}
